import React, { useState, useEffect } from 'react';
import { Plus, Search, X, DollarSign, Filter, TrendingUp, Flame, Star, Loader2 } from 'lucide-react';
import { addToWishlist, checkWishlistStatus, removeFromWishlist } from '../../WishlistModal';

const MerchPromoSection = ({ currentUser }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [filters, setFilters] = useState({
    sellerType: '',
    productType: '',
    genre: '',
    minPrice: '',
    maxPrice: '',
    tags: '',
    sort: 'newest'
  });
  const [newProduct, setNewProduct] = useState({
    seller_type: 'dj',
    product_name: '',
    description: '',
    product_type: 'apparel',
    price: '',
    external_link: '',
    image_url: '',
    genre: '',
    tags: []
  });
  const [userProductCount, setUserProductCount] = useState(0);
  const [showImageInfoPopup, setShowImageInfoPopup] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  // Fetch products
  const fetchProducts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (filters.sellerType) params.append('seller_type', filters.sellerType);
      if (filters.productType) params.append('product_type', filters.productType);
      if (filters.genre) params.append('genre', filters.genre);
      if (filters.minPrice) params.append('min_price', filters.minPrice);
      if (filters.maxPrice) params.append('max_price', filters.maxPrice);
      if (filters.tags) params.append('tags', filters.tags);
      if (searchQuery) params.append('search', searchQuery);
      params.append('sort', filters.sort);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/marketplace/merch?${params.toString()}`);
      const data = await response.json();
      setProducts(data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch user's product count
  const fetchUserProductCount = async () => {
    if (!currentUser) return;
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/marketplace/merch/user/${currentUser.id}`);
      const data = await response.json();
      setUserProductCount(data.length);
    } catch (error) {
      console.error('Failed to fetch user product count:', error);
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchUserProductCount();
  }, [filters, searchQuery]);

  // Handle product creation
  const handleCreateProduct = async () => {
    if (submitting) return; // Prevent double submission
    
    console.log('🔴 handleCreateProduct called', newProduct);
    
    // Validation
    if (!newProduct.product_name || !newProduct.description || !newProduct.price || !newProduct.external_link) {
      console.log('🔴 Validation failed:', {
        product_name: newProduct.product_name,
        description: newProduct.description,
        price: newProduct.price,
        external_link: newProduct.external_link
      });
      alert('Please fill in all required fields (Product Name, Description, Price, and External Link)');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const token = localStorage.getItem('beatseek_token');
      console.log('🔴 Token:', token ? 'exists' : 'missing');
      
      // Convert tags string to array if needed
      const productData = {
        ...newProduct,
        tags: typeof newProduct.tags === 'string' 
          ? newProduct.tags.split(',').map(t => t.trim()).filter(t => t)
          : newProduct.tags
      };
      
      console.log('🔴 Sending product data:', productData);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/marketplace/merch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(productData)
      });

      console.log('🔴 Response status:', response.status);
      
      if (response.ok) {
        const result = await response.json();
        console.log('🔴 Product created successfully:', result);
        setShowAddModal(false);
        setNewProduct({
          seller_type: 'dj',
          product_name: '',
          description: '',
          product_type: 'apparel',
          price: '',
          external_link: '',
          image_url: '',
          genre: '',
          tags: []
        });
        fetchProducts();
        fetchUserProductCount();
      } else {
        const errorData = await response.json();
        console.error('🔴 Failed to create product:', errorData);
        alert(`Failed to create product: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('🔴 Exception creating product:', error);
      alert('Failed to create product: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle product click (track and open link)
  const handleProductClick = async (product) => {
    try {
      // Track click
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/marketplace/merch/${product.id}/click`, {
        method: 'POST'
      });
      
      // Open external link
      window.open(product.external_link, '_blank');
    } catch (error) {
      console.error('Failed to track click:', error);
      window.open(product.external_link, '_blank');
    }
  };

  // Handle edit product
  const handleEditProduct = (product) => {
    setEditingProduct({
      ...product,
      tags: product.tags || []
    });
    setShowEditModal(true);
  };

  // Handle update product
  const handleUpdateProduct = async () => {
    // Validation
    if (!editingProduct.product_name || !editingProduct.description || !editingProduct.price || !editingProduct.external_link) {
      alert('Please fill in all required fields (Product Name, Description, Price, and External Link)');
      return;
    }

    try {
      const token = localStorage.getItem('beatseek_token');
      
      // Convert tags string to array if needed
      const tags = typeof editingProduct.tags === 'string' 
        ? editingProduct.tags.split(',').map(t => t.trim()).filter(t => t)
        : editingProduct.tags;
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/marketplace/merch/${editingProduct.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          product_name: editingProduct.product_name,
          description: editingProduct.description,
          product_type: editingProduct.product_type,
          price: editingProduct.price,
          external_link: editingProduct.external_link,
          image_url: editingProduct.image_url,
          genre: editingProduct.genre,
          tags: tags
        })
      });

      if (response.ok) {
        setShowEditModal(false);
        setEditingProduct(null);
        fetchProducts();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to update product');
      }
    } catch (error) {
      console.error('Failed to update product:', error);
      alert('Failed to update product');
    }
  };

  // Handle delete product
  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) {
      return;
    }

    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/marketplace/merch/${productId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        fetchProducts();
        fetchUserProductCount();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to delete product');
      }
    } catch (error) {
      console.error('Failed to delete product:', error);
      alert('Failed to delete product');
    }
  };

  return (
    <div style={{ padding: '2rem', color: '#fff' }}>
      {/* Header with Add Button */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '2rem' }}>
        {currentUser && (
          <button
            onClick={() => setShowAddModal(true)}
            disabled={userProductCount >= 10}
            style={{
              background: 'none',
              border: 'none',
              color: userProductCount >= 10 ? 'rgba(128, 128, 128, 0.5)' : '#00ffff',
              fontSize: '0.95rem',
              fontWeight: '600',
              cursor: userProductCount >= 10 ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
              transition: 'color 0.3s ease',
              padding: '0'
            }}
            onMouseEnter={(e) => {
              if (userProductCount < 10) {
                e.currentTarget.style.color = '#ff00ff';
              }
            }}
            onMouseLeave={(e) => {
              if (userProductCount < 10) {
                e.currentTarget.style.color = '#00ffff';
              }
            }}
          >
            <Plus size={18} />
            Add Product {userProductCount >= 10 && `(${userProductCount}/10 Max)`}
          </button>
        )}
      </div>

      {/* Search and Filters */}
      <div style={{ marginBottom: '2rem' }}>
        {/* Search Bar */}
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <Search size={20} style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: 'rgba(255, 255, 255, 0.5)' }} />
            <input
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem 1rem 0.75rem 3rem',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '1rem'
              }}
            />
          </div>

          <select
            value={filters.sort}
            onChange={(e) => setFilters({...filters, sort: e.target.value})}
            style={{
              padding: '0.75rem 1rem',
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '8px',
              color: '#fff',
              fontSize: '1rem',
              cursor: 'pointer'
            }}
          >
            <option value="newest" style={{ background: '#1e293b', color: '#fff' }}>Newest First</option>
            <option value="oldest" style={{ background: '#1e293b', color: '#fff' }}>Oldest First</option>
            <option value="price-low" style={{ background: '#1e293b', color: '#fff' }}>Price: Low to High</option>
            <option value="price-high" style={{ background: '#1e293b', color: '#fff' }}>Price: High to Low</option>
            <option value="trending" style={{ background: '#1e293b', color: '#fff' }}>🔥 Trending</option>
          </select>
        </div>

        {/* Filter Pills */}
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <select
            value={filters.sellerType}
            onChange={(e) => setFilters({...filters, sellerType: e.target.value})}
            style={{
              padding: '0.5rem 1rem',
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '20px',
              color: '#fff',
              fontSize: '0.9rem',
              cursor: 'pointer'
            }}
          >
            <option value="" style={{ background: '#1e293b', color: '#fff' }}>All Sellers</option>
            <option value="dj" style={{ background: '#1e293b', color: '#fff' }}>DJ</option>
            <option value="producer" style={{ background: '#1e293b', color: '#fff' }}>Producer</option>
            <option value="club" style={{ background: '#1e293b', color: '#fff' }}>Club/Venue</option>
            <option value="promoter" style={{ background: '#1e293b', color: '#fff' }}>Promoter</option>
          </select>

          <select
            value={filters.productType}
            onChange={(e) => setFilters({...filters, productType: e.target.value})}
            style={{
              padding: '0.5rem 1rem',
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '20px',
              color: '#fff',
              fontSize: '0.9rem',
              cursor: 'pointer'
            }}
          >
            <option value="" style={{ background: '#1e293b', color: '#fff' }}>All Products</option>
            <option value="apparel" style={{ background: '#1e293b', color: '#fff' }}>Apparel</option>
            <option value="stickers" style={{ background: '#1e293b', color: '#fff' }}>Stickers/Posters</option>
            <option value="digital" style={{ background: '#1e293b', color: '#fff' }}>Digital Products</option>
            <option value="other" style={{ background: '#1e293b', color: '#fff' }}>Other</option>
          </select>

          <input
            type="number"
            placeholder="Min Price"
            value={filters.minPrice}
            onChange={(e) => setFilters({...filters, minPrice: e.target.value})}
            style={{
              width: '120px',
              padding: '0.5rem 1rem',
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '20px',
              color: '#fff',
              fontSize: '0.9rem'
            }}
          />

          <input
            type="number"
            placeholder="Max Price"
            value={filters.maxPrice}
            onChange={(e) => setFilters({...filters, maxPrice: e.target.value})}
            style={{
              width: '120px',
              padding: '0.5rem 1rem',
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '20px',
              color: '#fff',
              fontSize: '0.9rem'
            }}
          />
        </div>
      </div>

      {/* Products Grid */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: 'rgba(255, 255, 255, 0.5)' }}>
          Loading products...
        </div>
      ) : products.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: 'rgba(255, 255, 255, 0.5)' }}>
          No products found. Be the first to add one!
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            gap: '1.25rem'
          }}
        >
          {products
            .sort((a, b) => {
              // Products with images come first, products without images go to the end
              const aHasImage = a.image_url && a.image_url.trim() !== '';
              const bHasImage = b.image_url && b.image_url.trim() !== '';
              
              if (aHasImage && !bHasImage) return -1;
              if (!aHasImage && bHasImage) return 1;
              return 0; // Keep original order if both have or don't have images
            })
            .map((product) => (
              <ProductCard 
                key={product.id} 
                product={product} 
                onClick={() => handleProductClick(product)}
                currentUser={currentUser}
                onEdit={() => handleEditProduct(product)}
                onDelete={() => handleDeleteProduct(product.id)}
              />
            ))}
        </div>
      )}

      {/* Add Product Modal */}
      {showAddModal && (
        <AddProductModal
          newProduct={newProduct}
          setNewProduct={setNewProduct}
          onClose={() => setShowAddModal(false)}
          onSubmit={handleCreateProduct}
          showImageInfoPopup={showImageInfoPopup}
          setShowImageInfoPopup={setShowImageInfoPopup}
          submitting={submitting}
        />
      )}

      {/* Edit Product Modal */}
      {showEditModal && editingProduct && (
        <AddProductModal
          newProduct={editingProduct}
          setNewProduct={setEditingProduct}
          onClose={() => {
            setShowEditModal(false);
            setEditingProduct(null);
          }}
          onSubmit={handleUpdateProduct}
          showImageInfoPopup={showImageInfoPopup}
          setShowImageInfoPopup={setShowImageInfoPopup}
          isEditing={true}
        />
      )}
    </div>
  );
};

// Product Card Component with Animated Gradient Border
const ProductCard = ({ product, onClick, currentUser, onEdit, onDelete }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [inWishlist, setInWishlist] = useState(false);
  const [wishlistItemId, setWishlistItemId] = useState(null);
  const [wishlistLoading, setWishlistLoading] = useState(false);
  const isOwner = currentUser && currentUser.id === product.user_id;
  const hasImage = product.image_url && product.image_url.trim() !== '';

  // Check wishlist status on mount
  useEffect(() => {
    const checkStatus = async () => {
      const status = await checkWishlistStatus('merch', product.id);
      setInWishlist(status.in_wishlist);
      setWishlistItemId(status.wishlist_item_id);
    };
    if (currentUser) {
      checkStatus();
    }
  }, [product.id, currentUser]);

  const handleWishlistToggle = async (e) => {
    e.stopPropagation();
    if (!currentUser) {
      alert('Please login to add items to your wishlist');
      return;
    }
    
    setWishlistLoading(true);
    try {
      if (inWishlist && wishlistItemId) {
        const success = await removeFromWishlist(wishlistItemId);
        if (success) {
          setInWishlist(false);
          setWishlistItemId(null);
        }
      } else {
        const merchData = {
          id: product.id,
          name: product.product_name,
          price: product.price,
          seller_name: product.user_name,
          product_type: product.product_type,
          external_link: product.external_link,
          image_url: product.image_url
        };
        const success = await addToWishlist('merch', merchData);
        if (success) {
          const status = await checkWishlistStatus('merch', product.id);
          setInWishlist(status.in_wishlist);
          setWishlistItemId(status.wishlist_item_id);
        }
      }
    } finally {
      setWishlistLoading(false);
    }
  };

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        position: 'relative',
        borderRadius: '12px',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        transform: isHovered ? 'translateY(-3px)' : 'translateY(0)',
        padding: '3px',
        background: isHovered
          ? 'linear-gradient(90deg, #00ffff, #ff00ff, #00ffff)'
          : 'linear-gradient(90deg, rgba(0, 255, 255, 0.3), rgba(255, 0, 255, 0.3), rgba(0, 255, 255, 0.3))',
        backgroundSize: '200% 200%',
        animation: 'flashyBorder 4s linear infinite',
        boxShadow: isHovered 
          ? '0 0 30px rgba(0, 255, 255, 0.6), 0 0 60px rgba(255, 0, 255, 0.4)'
          : '0 0 5px rgba(0, 255, 255, 0.2), 0 0 10px rgba(255, 0, 255, 0.1)'
      }}
    >
      <style>{`
        @keyframes flashyBorder {
          0% { background-position: 0% 50%; }
          100% { background-position: 200% 50%; }
        }
      `}</style>

      {/* Card Content */}
      <div
        style={{
          position: 'relative',
          background: 'linear-gradient(135deg, #0f172a, #1e293b, #0f172a)',
          borderRadius: '10px',
          height: '100%',
          overflow: 'hidden'
        }}
      >
        {/* Product Image - Only show if image exists */}
        {hasImage && (
          <div 
            onClick={onClick}
            style={{ position: 'relative', paddingTop: '100%', background: 'rgba(0, 0, 0, 0.3)' }}
          >
            <img
              src={product.image_url}
              alt={product.product_name}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          
            {/* Trending Badge */}
            {product.click_count > 50 && (
              <div
                style={{
                  position: 'absolute',
                  top: '0.5rem',
                  right: '0.5rem',
                  padding: '0.25rem 0.75rem',
                  background: 'rgba(255, 87, 34, 0.9)',
                  borderRadius: '20px',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem'
                }}
              >
                <Flame size={12} />
                Trending
              </div>
            )}
          </div>
        )}

        {/* Product Info */}
        <div onClick={onClick} style={{ padding: hasImage ? '0.75rem' : '1rem', cursor: 'pointer' }}>
          <h3
            style={{
              fontSize: '1rem',
              fontWeight: '700',
              margin: '0 0 0.4rem 0',
              color: '#fff',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {product.product_name}
          </h3>

          <p
            style={{
              fontSize: '0.8rem',
              color: 'rgba(255, 255, 255, 0.7)',
              margin: '0 0 0.6rem 0',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {product.description}
          </p>

          {/* Seller Info */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.6rem' }}>
            <span style={{ fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.5)' }}>
              by {product.user_name}
            </span>
            <span
              style={{
                padding: '0.25rem 0.5rem',
                background: 'rgba(255, 0, 255, 0.2)',
                border: '1px solid rgba(255, 0, 255, 0.4)',
                borderRadius: '12px',
                fontSize: '0.75rem',
                color: '#ff00ff'
              }}
            >
              {product.seller_type}
            </span>
          </div>

          {/* Tags */}
          {product.tags && product.tags.length > 0 && (
            <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
              {product.tags.slice(0, 2).map((tag, idx) => (
                <span
                  key={idx}
                  style={{
                    padding: '0.25rem 0.5rem',
                    background: 'rgba(0, 255, 255, 0.1)',
                    border: '1px solid rgba(0, 255, 255, 0.3)',
                    borderRadius: '12px',
                    fontSize: '0.7rem',
                    color: '#00ffff'
                  }}
                >
                  {tag}
                </span>
              ))}
            </div>
          )}

          {/* Price and Actions */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '1.25rem', fontWeight: '700', color: '#00ffff' }}>
                ${product.price}
              </span>
              
              {/* Wishlist Star - Always visible for non-owners */}
              {currentUser && !isOwner && (
                <button
                  onClick={handleWishlistToggle}
                  disabled={wishlistLoading}
                  data-testid={`merch-wishlist-${product.id}`}
                  style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    background: inWishlist 
                      ? 'rgba(255, 215, 0, 0.9)' 
                      : 'rgba(255, 215, 0, 0.15)',
                    border: inWishlist 
                      ? '2px solid #ffd700' 
                      : '2px solid rgba(255, 215, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: wishlistLoading ? 'wait' : 'pointer',
                    transition: 'all 0.2s ease',
                    opacity: wishlistLoading ? 0.5 : 1,
                    padding: 0
                  }}
                  title={inWishlist ? 'Remove from Wishlist' : 'Add to Wishlist'}
                >
                  {wishlistLoading ? (
                    <Loader2 size={12} className="animate-spin" style={{ color: '#ffd700' }} />
                  ) : (
                    <Star 
                      size={14} 
                      fill={inWishlist ? '#000' : 'none'} 
                      color={inWishlist ? '#000' : '#ffd700'} 
                    />
                  )}
                </button>
              )}
            </div>
            
            {isOwner ? (
              /* Edit/Delete buttons for owner */
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit();
                  }}
                  style={{
                    padding: '0.4rem 0.8rem',
                    background: 'rgba(0, 255, 255, 0.2)',
                    border: '1px solid rgba(0, 255, 255, 0.4)',
                    borderRadius: '6px',
                    color: '#00ffff',
                    fontSize: '0.8rem',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Edit
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete();
                  }}
                  style={{
                    padding: '0.4rem 0.8rem',
                    background: 'rgba(255, 0, 0, 0.2)',
                    border: '1px solid rgba(255, 0, 0, 0.4)',
                    borderRadius: '6px',
                    color: '#ff0000',
                    fontSize: '0.8rem',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Delete
                </button>
              </div>
            ) : (
              /* Buy Now button for non-owners */
              <button
                onClick={onClick}
                style={{
                  padding: '0.4rem 0.85rem',
                  background: 'linear-gradient(135deg, #ff00ff, #00ffff)',
                  border: 'none',
                  borderRadius: '6px',
                  color: '#fff',
                  fontSize: '0.85rem',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Buy Now →
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Add Product Modal Component
const AddProductModal = ({ newProduct, setNewProduct, onClose, onSubmit, showImageInfoPopup, setShowImageInfoPopup, isEditing = false, submitting = false }) => {
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.9)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: '1rem'
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'rgba(26, 26, 46, 0.98)',
          borderRadius: '20px',
          border: '2px solid rgba(255, 0, 255, 0.5)',
          maxWidth: '600px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header - Fixed */}
        <div style={{ padding: '2rem 2rem 0 2rem', flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <h3 style={{ fontSize: '1.5rem', fontWeight: '700', margin: 0 }}>{isEditing ? 'Edit Product' : 'Add New Product'}</h3>
            <button
              onClick={onClose}
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                background: 'rgba(255, 0, 128, 0.2)',
                border: '1px solid rgba(255, 0, 128, 0.4)',
                color: '#ff0080',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer'
              }}
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Form - Scrollable */}
        <div style={{ 
          padding: '0 2rem 2rem 2rem', 
          overflowY: 'auto', 
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem'
        }}>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.8)' }}>
              Product Name *
            </label>
            <input
              type="text"
              value={newProduct.product_name}
              onChange={(e) => setNewProduct({...newProduct, product_name: e.target.value})}
              style={{
                width: '100%',
                padding: '0.75rem',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '1rem'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.8)' }}>
              Description (Max 100 chars) *
            </label>
            <input
              type="text"
              maxLength={100}
              value={newProduct.description}
              onChange={(e) => setNewProduct({...newProduct, description: e.target.value})}
              style={{
                width: '100%',
                padding: '0.75rem',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '1rem'
              }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.8)' }}>
                Seller Type *
              </label>
              <select
                value={newProduct.seller_type}
                onChange={(e) => setNewProduct({...newProduct, seller_type: e.target.value})}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '1rem'
                }}
              >
                <option value="dj" style={{ background: '#1e293b', color: '#fff' }}>DJ</option>
                <option value="producer" style={{ background: '#1e293b', color: '#fff' }}>Producer</option>
                <option value="club" style={{ background: '#1e293b', color: '#fff' }}>Club/Venue</option>
                <option value="promoter" style={{ background: '#1e293b', color: '#fff' }}>Promoter</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.8)' }}>
                Product Type *
              </label>
              <select
                value={newProduct.product_type}
                onChange={(e) => setNewProduct({...newProduct, product_type: e.target.value})}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '1rem'
                }}
              >
                <option value="apparel" style={{ background: '#1e293b', color: '#fff' }}>Apparel</option>
                <option value="stickers" style={{ background: '#1e293b', color: '#fff' }}>Stickers/Posters</option>
                <option value="digital" style={{ background: '#1e293b', color: '#fff' }}>Digital Products</option>
                <option value="other" style={{ background: '#1e293b', color: '#fff' }}>Other</option>
              </select>
            </div>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.8)' }}>
              Price (USD) *
            </label>
            <input
              type="number"
              step="0.01"
              value={newProduct.price}
              onChange={(e) => setNewProduct({...newProduct, price: e.target.value})}
              style={{
                width: '100%',
                padding: '0.75rem',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '1rem'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.8)' }}>
              External Link (Where to buy) *
            </label>
            <input
              type="url"
              value={newProduct.external_link}
              onChange={(e) => setNewProduct({...newProduct, external_link: e.target.value})}
              placeholder="https://your-store.com/product"
              style={{
                width: '100%',
                padding: '0.75rem',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '1rem'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.8)' }}>
              Product Image URL (Optional)
              <button
                type="button"
                onClick={() => setShowImageInfoPopup(!showImageInfoPopup)}
                onMouseEnter={() => setShowImageInfoPopup(true)}
                onMouseLeave={() => setShowImageInfoPopup(false)}
                style={{
                  width: '18px',
                  height: '18px',
                  borderRadius: '50%',
                  background: 'rgba(0, 255, 255, 0.2)',
                  border: '1px solid rgba(0, 255, 255, 0.4)',
                  color: '#00ffff',
                  fontSize: '0.75rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold'
                }}
              >
                i
              </button>
            </label>
            {showImageInfoPopup && (
              <div style={{
                padding: '0.75rem',
                background: 'rgba(0, 255, 255, 0.1)',
                border: '1px solid rgba(0, 255, 255, 0.3)',
                borderRadius: '8px',
                marginBottom: '0.5rem',
                fontSize: '0.85rem',
                color: 'rgba(255, 255, 255, 0.9)',
                lineHeight: '1.5'
              }}>
                <strong>📸 How to add a product image:</strong>
                <br/><br/>
                → Right-click your product image on your store
                <br/>→ Select &quot;Copy image address&quot;
                <br/>→ Paste the URL here
                <br/><br/>
                Example: https://i.imgur.com/abc123.jpg
              </div>
            )}
            <input
              type="url"
              value={newProduct.image_url}
              onChange={(e) => setNewProduct({...newProduct, image_url: e.target.value})}
              placeholder="https://example.com/image.jpg"
              style={{
                width: '100%',
                padding: '0.75rem',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '1rem'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.8)' }}>
              Genre (Optional)
            </label>
            <input
              type="text"
              value={newProduct.genre}
              onChange={(e) => setNewProduct({...newProduct, genre: e.target.value})}
              placeholder="e.g., House, Techno, etc."
              style={{
                width: '100%',
                padding: '0.75rem',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '1rem'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.8)' }}>
              Tags (Comma-separated, optional)
            </label>
            <input
              type="text"
              value={Array.isArray(newProduct.tags) ? newProduct.tags.join(', ') : newProduct.tags || ''}
              onChange={(e) => setNewProduct({...newProduct, tags: e.target.value})}
              placeholder="limited-edition, sale, vintage"
              style={{
                width: '100%',
                padding: '0.75rem',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '1rem'
              }}
            />
          </div>

          <button
            onClick={onSubmit}
            disabled={submitting}
            style={{
              padding: '0.75rem 1.5rem',
              background: submitting 
                ? 'rgba(128, 128, 128, 0.5)' 
                : 'linear-gradient(135deg, #ff00ff, #00ffff)',
              border: 'none',
              borderRadius: '8px',
              color: '#fff',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: submitting ? 'wait' : 'pointer',
              marginTop: '1rem',
              opacity: submitting ? 0.7 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem'
            }}
          >
            {submitting ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                {isEditing ? 'Updating...' : 'Adding...'}
              </>
            ) : (
              isEditing ? 'Update Product' : 'Add Product'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MerchPromoSection;
